import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, keyframes, Menu, MenuItem, Typography } from "@mui/material";
import { Delete, EditDocument, MoreVert, Note, Publish, Sync, AccessTime, Lock, Public } from "@mui/icons-material";
import { useState } from "react";
import { truncateString } from "../../../../utils/truncate-string";
import { useNavigate } from "react-router-dom";
import { Lecture, LectureAccess } from "../../../../models/lecture";
import DeleteLectureDialog from "./delete-lecture-dialog";
import formatDateTime from "../../../../utils/get-local-date-time";

interface LectureCardProp {
    lecture: Lecture;
    onDelete: (lecture_id: number) => void
    runningTask: number;
}

export default function LectureCard({ lecture, onDelete, runningTask }: LectureCardProp) {
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

    const navigate = useNavigate();

    const [firstPublishDialog, setFirstPublishDialog] = useState(false);

    const [deleteLectureDialog, setDeleteLectureDialog] = useState(false);

    const getLectureStatus = () => {
        if (!lecture.start_time || !lecture.duration) {
            return { status: 'scheduled', label: 'NO TIME SET', color: '#6b7280' };
        }
        
        const now = new Date();
        const startTime = new Date(lecture.start_time);
        const endTime = new Date(startTime.getTime() + (lecture.duration * 60 * 1000)); // duration in minutes to milliseconds
        
        if (now < startTime) {
            return { status: 'awaiting', label: 'AWAITING', color: '#f59e0b' };
        } else if (now >= startTime && now <= endTime) {
            return { status: 'live', label: 'LIVE', color: '#ef4444' };
        } else {
            return { status: 'ended', label: 'ENDED', color: '#6b7280' };
        }
    };

    const lectureStatus = getLectureStatus();
    const startTimeFormatted = lecture.start_time ? formatDateTime(lecture.start_time) : null;

  
    return (
        <Box
            sx={{
                padding: 2,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 2,
                transition: 'all 0.3s ease',
                backgroundColor: "white",
                border: "1px solid",
                borderColor: lectureStatus.status === 'live' 
                    ? "success.main" 
                    : lectureStatus.status === 'awaiting' 
                    ? "#f59e0b" 
                    : "grey.300",
                cursor: 'pointer',
                position: 'relative',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                },
                height: "100px"
            }}
            onClick={() => {
                if (deleteLectureDialog || firstPublishDialog || runningTask === lecture.id) return;
                navigate(`/classroom/${lecture.classroom_id}/lectures/${lecture.id}`);
            }}            
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                    <Typography 
                        variant="subtitle2"
                        sx={{
                            fontWeight: 'bold',
                            marginBottom: '4px',
                            lineHeight: 1.2
                        }}
                    >
                        {truncateString(lecture.title, 25)}
                    </Typography>

                    {startTimeFormatted && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <AccessTime sx={{ fontSize: 12, color: '#64748b' }} />
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: '#64748b',
                                    fontSize: '0.7rem',
                                }}
                            >
                                {startTimeFormatted.date} at {startTimeFormatted.time}
                            </Typography>
                        </Box>
                    )}

                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: '#64748b',
                            fontSize: '0.65rem',
                        }}
                    >
                        Duration: {lecture.duration} mins
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Lecture Status Chip */}
                    <Chip
                        label={lectureStatus.label}
                        size="small"
                        sx={{
                            backgroundColor: lectureStatus.status === 'live' ? '#fee2e2' : 
                                           lectureStatus.status === 'awaiting' ? '#fef3c7' : '#f3f4f6',
                            color: lectureStatus.color,
                            fontWeight: 600,
                            fontSize: '0.6rem',
                            height: 24,
                            '& .MuiChip-label': {
                                px: 1.5,
                            },
                            ...(lectureStatus.status === 'live' && {
                                animation: 'pulse 2s infinite'
                            })
                        }}
                    />

                    {/* Access Level Chip */}
                    <Chip
                        icon={lecture.access === LectureAccess.PUBLIC ? <Public sx={{ fontSize: 12 }} /> : <Lock sx={{ fontSize: 12 }} />}
                        label={lecture.access === LectureAccess.PUBLIC ? "PUBLIC" : "PRIVATE"}
                        size="small"
                        sx={{
                            backgroundColor: lecture.access === LectureAccess.PUBLIC ? '#dbeafe' : '#f3f4f6',
                            color: lecture.access === LectureAccess.PUBLIC ? '#1d4ed8' : '#6b7280',
                            fontWeight: 500,
                            fontSize: '0.6rem',
                            height: 24,
                            '& .MuiChip-label': {
                                px: 1,
                            },
                            '& .MuiChip-icon': {
                                marginLeft: 1,
                                marginRight: -0.5,
                            }
                        }}
                    />

                    {/* Recording indicator */}
                    {lecture.record && (
                        <Chip
                            label="REC"
                            size="small"
                            sx={{
                                backgroundColor: '#fecaca',
                                color: '#dc2626',
                                fontWeight: 600,
                                fontSize: '0.6rem',
                                height: 24,
                                '& .MuiChip-label': {
                                    px: 1.5,
                                },
                            }}
                        />
                    )}
                </Box>

                {runningTask === lecture.id ? (
                  <CircularProgress size={18} />
                ) : (
                    <IconButton
                        onClick={(event) => {
                            event.stopPropagation();
                            setDeleteLectureDialog(true);
                        }}
                        sx={{ 
                            width: 25, 
                            height: 25, 
                            backgroundColor: '#fccac5',
                            borderRadius: 2,
                            '&:hover': {
                                backgroundColor: '#fecaca',
                                transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s ease'
                        }}
                        title="Delete Lecture"
                    >
                        <Delete sx={{ fontSize: 16, color: '#dc2626' }} />
                    </IconButton>
                )}
            </Box>

            <DeleteLectureDialog
                lecture={lecture}
                open={deleteLectureDialog}
                setOpen={setDeleteLectureDialog}
                deleteFunction={onDelete}
            />
        </Box>
    );
}